import { set, get, size } from 'global/lib/lodash';
import { reduxStore } from 'index';
import { UserPermission } from '../lib/filter';
import moment from 'moment';

// Assessments tabs (Browse / Data / Photos) — filter.reports.*
export const AssessmentFilter = (overrides = {}) => ({
  searchstring: '',
  scorecard_id: 0,
  start_date: null,
  end_date: null,
  realm_type: [], //'division', 'community', 'lot'
  client_id: null,
  vendor_id: null,
  daterange: 'none',

  //any keys that start with _ will be pulled out of the filters
  _sort: {
    sortBy: 'relevance',
  },
  ...overrides,
});

export const TaskFilter = (overrides = {}) => ({
  searchstring: '',
  ownership: 'my',
  realm_type: [],
  status: 'openPending',
  client_id: null,
  vendor_id: null,

  _sort: {
    sortBy: '-updated_at',
  },
  ...overrides,
});

export const UserFilter = (overrides = {}) => ({
  searchstring: '',
  status: 'active',
  permission: [],
  user_group_id: [],
  client_id: 0,

  ...overrides,
});

export const EditionsFilter = (overrides = {}) => ({
  searchstring: '',
  status: ['active'],
  courseStatus: [null, [0, 99]],
  edition_id: 0,
  start_date: null,
  end_date: null,
  role: null,
  user_groups: [],

  _sort: {},
  ...overrides,
});

export const StandardsFilter = (overrides = {}) => ({
  searchstring: '',
  scorecard: 0,
  doctypes: ['type1', 'type2', 'type3', 'type4'],
  supersets: ['super1', 'super2', 'super3', 'super4'],

  _sort: {
    sortBy: 0,
  },
  ...overrides,
});

// Per-screen defaults. Keys on each screen are the source of truth for tab sync
// (intersection of keys, excluding searchstring and _*). Only reports/tasks tabs sync.
export const FILTERDEFAULTS = {
  reports: {
    list: AssessmentFilter({ template_id: 0 }),
    data: AssessmentFilter({
      template_id: 0,
      edition_id: 0,
      status: 'active',
      realms: [],
      compareStatus: 'active',
      compareType: 'diff',
      _showCompare: false,
    }),
    photo: AssessmentFilter({
      superset_id: null,
      user_id: null,
      quality_type: 1,
      section_type: null,
      template: null,
      _photoSource: 'all',
      _includeRealm: true,
    }),
  },
  tasks: {
    list: TaskFilter({ _sort: { sortBy: 'updated_at' } }),
    scorecard: TaskFilter({
      content: 'title',
      scorecard_id: [0],
      trade_types: [0],
      task_type_ids: [0],
      _sort: { sortBy: 'updated_at' },
    }),
    realm: TaskFilter({ ownership: 'all' }),
  },
  library: {
    editions: EditionsFilter({ useLocation: true }),
    courses: EditionsFilter(),
    standards: StandardsFilter(),
  },
  users: {
    manage: UserFilter(),
    courses: UserFilter({ report_type: 'user' }),
  },
  alerts: {
    user: { locationOn: false },
    all: { locationOn: false },
  },
};

const syncFilterCategories = ['reports', 'tasks'];

export const filterSyncScreens = (category) => {
  if (syncFilterCategories.indexOf(category) === -1) {
    return null;
  }
  const group = get(FILTERDEFAULTS, category);
  return group ? Object.keys(group) : null;
};

// Keys owned by a screen (from FILTERDEFAULTS).
export const screenFilterKeys = (category, screen) => {
  const defaults = get(FILTERDEFAULTS, [category, screen]);
  return defaults ? Object.keys(defaults) : [];
};

// Sibling sync: keys the target screen owns that are present on the update payload.
// Matches prior matchInFilter behavior (target hasOwnProperty), using FILTERDEFAULTS
// as the source of truth — so data-only keys like `realms` can still be seeded when
// written from another tab's Filters payload, without persisting on list/photo.
export const syncableFilterKeys = (category, screen, data = {}) => {
  return screenFilterKeys(category, screen).filter((k) => k !== 'searchstring' && k[0] !== '_' && Object.prototype.hasOwnProperty.call(data, k));
};

export const buildFilters = (filter = {}, opts = {}) => {
  let filterRet = {};
  let sort = [];
  const extras = {};

  const sState = reduxStore.store.getState();
  const realmId = get(sState, 'auth.realm.id');
  const usersRealm = get(sState, 'auth.user.realm.id');

  for (let k in filter) {
    if (k.startsWith('_')) {
      set(extras, k.replace('_', ''), filter[k]);
    } else {
      let item = filter[k];
      if (item || item === 0) {
        if (Array.isArray(item)) {
          if (item.length > 0) {
            set(filterRet, k, item.map((ii) => get(ii, 'id', ii) || ii).join(','));
          }
        } else if (typeof item === 'object' && Object.keys(item).length && !(item instanceof moment)) {
          //typically we are planning for these to be checkboxes
          const list = [];
          for (let _k in item) {
            const ki = item[_k];
            if (ki) {
              list.push(_k);
            }
          }
          if (list.length > 0) {
            set(filterRet, k, list.join(','));
          }
        } else {
          if (k.indexOf('_date') > -1) {
            const isValid = moment(item).isValid();
            item = isValid ? moment(item).toISOString() : undefined;
          }
          set(filterRet, k, item);
        }
      }
    }
  }

  if (opts.includeRealm && realmId !== undefined) {
    set(filterRet, 'realm', realmId);
  }

  if (opts.includeUsersRealm && usersRealm !== undefined) {
    set(filterRet, 'realm_id', usersRealm);
  }

  if (opts.convertFilterToSort) {
    opts.convertFilterToSort.forEach((fts) => {
      const item = get(filterRet, fts);
      if (item) {
        delete filterRet[fts];
        sort.push(item);
      }
    });
  }

  if (opts.convertName) {
    for (let k in opts.convertName) {
      if (filterRet[k] !== undefined) {
        const val = filterRet[k];
        if (Array.isArray(opts.convertName[k])) {
          // eslint-disable-next-line no-loop-func
          opts.convertName[k].map((str) => {
            filterRet[str] = val;
          });
        } else {
          filterRet[opts.convertName[k]] = val;
        }
        delete filterRet[k];
      }
    }
  }

  opts.dontFilter = ['daterange', ...(opts.dontFilter || [])];
  if (opts.dontFilter) {
    opts.dontFilter.forEach((df) => {
      delete filterRet[df];
    });
  }

  if (extras.sort) {
    const sortBy = get(extras, 'sort.sortBy');
    if (sortBy !== undefined) {
      sort.push(sortBy);
    }
  }

  if (opts.dontSort) {
    if (typeof opts.dontSort === 'boolean') {
      sort = opts.dontSort ? [] : sort;
    } else if (Array.isArray(opts.dontSort)) {
      sort = sort.filter((s) => opts.dontSort.indexOf(s) === -1);
    }
  }

  if (opts.overrideSort) {
    sort = opts.overrideSort(sort);
  }

  if (opts.overrideFilter) {
    filterRet = opts.overrideFilter(filterRet);
  }

  return {
    filter: filterRet,
    sort: Array.isArray(sort) && !!size(sort) ? sort.join(',') : undefined,
  };
};

export const appPermissionsToServerPermissions = (filter) => {
  if (filter.permission) {
    const sState = reduxStore.store.getState();
    const perms = filter.permission.split(',');
    const paramArr = perms.map(
      (p) =>
        `${get(
          UserPermission.find((up) => up.value === p),
          'match'
        )}.${get(sState, 'auth.user.realm.id')}`
    );
    set(filter, 'name', paramArr.join(','));
  }
};
